/**
 * useTopDownCamera — toggle a runway-aligned top-down view of the airfield.
 *
 * On the rising edge of `active` it snapshots the current camera, then eases to
 * pitch 0, bearing = runway bearing, framed on the bbox of the apron zones ∪ the
 * runway. On the falling edge it eases back to the snapshot, so the user is never
 * stranded in the flattened view. Scene-feet geometry is converted to lng/lat via
 * CoordinateService and fit with `map.cameraForBounds`.
 */
import { useEffect, useRef } from 'react'
import { CoordinateService } from '@/services/3d/CoordinateService'
import type { FieldGraph } from '../data/fieldGraph'
import type { ZoneGeometry } from '../engine/types'

interface CameraSnapshot {
  pitch: number
  bearing: number
  zoom: number
  center: mapboxgl.LngLat
}

interface Options {
  zones: ZoneGeometry[]
  fieldGraph: FieldGraph | undefined
  /** Scene origin [lng, lat]. */
  center: [number, number] | null
}

export function useTopDownCamera(
  map: mapboxgl.Map | null,
  active: boolean,
  { zones, fieldGraph, center }: Options
) {
  const saved = useRef<CameraSnapshot | null>(null)
  const wasActive = useRef(false)

  useEffect(() => {
    if (!map || !center) return

    if (active && !wasActive.current) {
      wasActive.current = true
      saved.current = {
        pitch: map.getPitch(),
        bearing: map.getBearing(),
        zoom: map.getZoom(),
        center: map.getCenter(),
      }

      // Collect apron zone corners + runway/spine extents in scene feet.
      const ptsFeet: [number, number][] = []
      for (const z of zones) {
        const hx = z.width / 2
        const hz = z.depth / 2
        ptsFeet.push([z.center[0] - hx, z.center[1] - hz], [z.center[0] + hx, z.center[1] + hz])
      }
      if (fieldGraph) {
        ptsFeet.push(fieldGraph.thresholdApproach, fieldGraph.thresholdDeparture, fieldGraph.spine[0], fieldGraph.spine[1])
      }

      const bearing = fieldGraph?.runwayBearingDeg ?? 0
      if (ptsFeet.length === 0) {
        map.easeTo({ pitch: 0, bearing, duration: 800 })
        return
      }

      let minLng = Infinity
      let minLat = Infinity
      let maxLng = -Infinity
      let maxLat = -Infinity
      for (const [x, z] of ptsFeet) {
        const [lng, lat] = CoordinateService.scenePositionToLngLat([x, 0, z], center)
        minLng = Math.min(minLng, lng)
        maxLng = Math.max(maxLng, lng)
        minLat = Math.min(minLat, lat)
        maxLat = Math.max(maxLat, lat)
      }

      const cam = map.cameraForBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { bearing, padding: 80 }
      )
      if (cam) {
        map.easeTo({ center: cam.center, zoom: cam.zoom, pitch: 0, bearing, duration: 800 })
      } else {
        map.easeTo({ pitch: 0, bearing, duration: 800 })
      }
    } else if (!active && wasActive.current) {
      wasActive.current = false
      const s = saved.current
      if (s) {
        map.easeTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom, center: s.center, duration: 800 })
        saved.current = null
      }
    }
  }, [map, active, center, zones, fieldGraph])
}
